/**
 * VoiceAgentButton — browser-to-ACS VoIP call to the SpocoDoc AI agent.
 *
 * Uses @azure/communication-calling (browser SDK).
 * The backend issues a short-lived ACS token tied to the logged-in user.
 * ACS routes the call to our Call Automation webhook — no phone number needed.
 */

import { useState, useRef, useCallback, useEffect } from 'react';
import { Phone, Mic, MicOff, PhoneOff, PhoneCall, Loader2, AlertTriangle, AlertOctagon } from 'lucide-react';
import { CallClient } from '@azure/communication-calling';
import { AzureCommunicationTokenCredential } from '@azure/communication-common';
import apiClient from '../api/client';

const STATES = {
  IDLE:        'idle',
  CONNECTING:  'connecting',
  RINGING:     'ringing',
  ACTIVE:      'active',
  ERROR:       'error',
};

export default function VoiceAgentButton({ mobileOnly = false }) {
  const [status,      setStatus]      = useState(STATES.IDLE);
  const [muted,       setMuted]       = useState(false);
  const [errMsg,      setErrMsg]      = useState('');
  const [quotaStatus, setQuotaStatus] = useState(null); // { status: 'ok'|'warning'|'blocked', message }
  const [isMobile,    setIsMobile]    = useState(() =>
    typeof window !== 'undefined' && window.matchMedia('(max-width: 1023px)').matches
  );

  const callClientRef  = useRef(null);
  const callAgentRef   = useRef(null);
  const currentCall    = useRef(null);
  const callInProgress = useRef(false); // true = ACS call is active (mic in use)

  const cleanup = useCallback(() => {
    callInProgress.current = false;
    currentCall.current = null;
    if (callAgentRef.current) {
      callAgentRef.current.dispose().catch(() => {});
      callAgentRef.current = null;
    }
    callClientRef.current = null;
  }, []);

  const startCall = useCallback(async () => {
    if (status !== STATES.IDLE) return;
    setStatus(STATES.CONNECTING);
    setErrMsg('');
    callInProgress.current = true;

    try {
      // 1. Get ACS token from backend (tied to logged-in SpocoDoc user)
      const { data } = await apiClient.get('/voice/token');

      // Surface soft-quota warning returned alongside the token
      if (data.quotaWarning) {
        setQuotaStatus({ status: 'warning', message: data.quotaWarning });
      }

      if (!data.botUserId) {
        throw new Error('Voice agent not configured yet. Ask your admin to restart the server.');
      }

      // 2. Initialise ACS Calling SDK
      if (!callClientRef.current) callClientRef.current = new CallClient();
      const credential = new AzureCommunicationTokenCredential(data.token);
      callAgentRef.current = await callClientRef.current.createCallAgent(credential, {
        displayName: 'SpocoDoc User',
      });

      // 3. Call the bot user
      setStatus(STATES.RINGING);
      currentCall.current = callAgentRef.current.startCall(
        [{ communicationUserId: data.botUserId }]
      );

      // 4. Track call state
      currentCall.current.on('stateChanged', () => {
        const s = currentCall.current?.state;
        if (s === 'Connected')                            setStatus(STATES.ACTIVE);
        if (s === 'Disconnected' || s === 'Disconnecting') { setStatus(STATES.IDLE); cleanup(); }
      });

    } catch (e) {
      console.error('[Voice] startCall error:', e);
      setErrMsg(e.message || 'Could not connect');
      setStatus(STATES.ERROR);
      callInProgress.current = false;
      cleanup();
      setTimeout(() => setStatus(STATES.IDLE), 4000);
    }
  }, [status, cleanup]);

  const hangUp = useCallback(async () => {
    try { await currentCall.current?.hangUp(); } catch (_) {}
    cleanup();
    setStatus(STATES.IDLE);
    setMuted(false);
  }, [cleanup]);

  const toggleMute = useCallback(async () => {
    if (!currentCall.current) return;
    try {
      if (muted) await currentCall.current.unmute();
      else       await currentCall.current.mute();
      setMuted(m => !m);
    } catch (_) {}
  }, [muted]);

  // Poll quota status when idle so the banner shows before the user clicks Call
  useEffect(() => {
    if (status !== STATES.IDLE) return;
    apiClient.get('/voice/quota-status')
      .then(r => setQuotaStatus(r.data))
      .catch(() => {}); // non-fatal — don't break the button if this fails
  }, [status]);

  // Track mobile breakpoint
  useEffect(() => {
    const mq = window.matchMedia('(max-width: 1023px)');
    const handler = e => setIsMobile(e.matches);
    mq.addEventListener('change', handler);
    return () => mq.removeEventListener('change', handler);
  }, []);



  // ── Mobile FAB (mobileOnly=true) ─────────────────────────────
  if (mobileOnly) {
    const isBlocked = quotaStatus?.status === 'blocked';
    const isWarning = quotaStatus?.status === 'warning';
    const quotaBanner = (isBlocked || isWarning) ? (
      <div className={`fixed bottom-28 right-4 z-50 max-w-xs rounded-xl shadow-lg border px-3 py-2 text-xs flex items-start gap-2 ${
        isBlocked ? 'bg-red-50 border-red-200 text-red-700' : 'bg-amber-50 border-amber-200 text-amber-700'
      }`}>
        {isBlocked ? <AlertOctagon className="w-4 h-4 mt-0.5 shrink-0" /> : <AlertTriangle className="w-4 h-4 mt-0.5 shrink-0" />}
        <span>{quotaStatus.message}</span>
      </div>
    ) : null;
    if (status === STATES.ACTIVE) {
      return (
        <div className="fixed bottom-8 right-4 z-50 flex flex-col items-end gap-2">
          <div className="flex items-center gap-2 bg-white rounded-2xl shadow-2xl border border-gray-200 px-4 py-3">
            <span className="flex items-center gap-1.5 text-xs font-semibold text-green-600 mr-1">
              <span className="w-2 h-2 rounded-full bg-green-500 animate-pulse" />
              Live
            </span>
            <button onClick={toggleMute} title={muted ? 'Unmute' : 'Mute'}
              className={`w-11 h-11 rounded-full flex items-center justify-center transition-all active:scale-95 ${
                muted ? 'bg-amber-100 text-amber-600' : 'bg-blue-100 text-blue-600'
              }`}>
              {muted ? <MicOff className="h-5 w-5" /> : <Mic className="h-5 w-5" />}
            </button>
            <button onClick={hangUp} title="End call"
              className="w-11 h-11 rounded-full bg-red-500 hover:bg-red-600 active:scale-95 flex items-center justify-center text-white transition-all">
              <PhoneOff className="h-5 w-5" />
            </button>
          </div>
        </div>
      );
    }

    // Connecting / ringing
    if (status === STATES.CONNECTING || status === STATES.RINGING) {
      return (
        <div className="fixed bottom-8 right-4 z-50">
          <button disabled title={status === STATES.RINGING ? 'Ringing…' : 'Connecting…'}
            className="w-14 h-14 rounded-full bg-blue-400 shadow-xl flex items-center justify-center text-white animate-pulse">
            <Loader2 className="h-6 w-6 animate-spin" />
          </button>
        </div>
      );
    }

    // Error
    if (status === STATES.ERROR) {
      return (
        <div className="fixed bottom-8 right-4 z-50">
          <button disabled title={errMsg}
            className="w-14 h-14 rounded-full bg-red-500 shadow-xl flex items-center justify-center text-white">
            <PhoneOff className="h-6 w-6" />
          </button>
        </div>
      );
    }

    // Idle — big blue phone FAB (blocked = grey + disabled)
    return (
      <>
        {quotaBanner}
        <div className="fixed bottom-8 right-4 z-50">
          {isBlocked ? (
            <button disabled title={quotaStatus.message}
              className="w-14 h-14 rounded-full bg-gray-400 shadow-xl flex items-center justify-center text-white cursor-not-allowed">
              <AlertOctagon className="h-6 w-6" />
            </button>
          ) : (
            <button onClick={startCall} title="Call AI Agent"
              className="w-14 h-14 rounded-full bg-blue-600 hover:bg-blue-700 active:scale-95 shadow-xl flex items-center justify-center text-white transition-all">
              <Phone className="h-6 w-6" />
            </button>
          )}
        </div>
      </>
    );
  }

  // ── Idle: handset button (desktop) ──
  if (status === STATES.IDLE) {
    const isBlocked = quotaStatus?.status === 'blocked';
    const isWarning = quotaStatus?.status === 'warning';
    return (
      <div className="relative group">
        <button
          onClick={isBlocked ? undefined : startCall}
          disabled={isBlocked}
          title={isBlocked ? quotaStatus.message : isWarning ? quotaStatus.message : 'Call AI Agent (or say "Call SpocoDoc")'}
          className={`relative p-2 rounded-lg transition-colors ${
            isBlocked
              ? 'text-gray-400 cursor-not-allowed'
              : isWarning
                ? 'text-amber-500 hover:text-amber-600 hover:bg-amber-50'
                : 'text-gray-500 dark:text-gray-300 hover:text-blue-600 dark:hover:text-blue-400 hover:bg-blue-50 dark:hover:bg-blue-900/30'
          }`}
        >
          {isBlocked ? <AlertOctagon className="h-5 w-5" /> : isWarning ? <AlertTriangle className="h-5 w-5" /> : <Phone className="h-5 w-5" />}
        </button>
        {(isBlocked || isWarning) && (
          <div className={`absolute right-0 top-9 z-50 w-64 rounded-xl shadow-lg border px-3 py-2 text-xs hidden group-hover:flex items-start gap-2 ${
            isBlocked ? 'bg-red-50 border-red-200 text-red-700' : 'bg-amber-50 border-amber-200 text-amber-700'
          }`}>
            {isBlocked ? <AlertOctagon className="w-3.5 h-3.5 mt-0.5 shrink-0" /> : <AlertTriangle className="w-3.5 h-3.5 mt-0.5 shrink-0" />}
            <span>{quotaStatus.message}</span>
          </div>
        )}
      </div>
    );
  }

  // ── Connecting / ringing: spinner ──
  if (status === STATES.CONNECTING || status === STATES.RINGING) {
    return (
      <button disabled title={status === STATES.RINGING ? 'Ringing…' : 'Connecting…'}
        className="relative p-2 text-blue-500 animate-pulse rounded-lg"
      >
        <Loader2 className="h-5 w-5 animate-spin" />
      </button>
    );
  }

  // ── Error ──
  if (status === STATES.ERROR) {
    return (
      <button disabled title={errMsg}
        className="relative p-2 text-red-500 rounded-lg"
      >
        <PhoneOff className="h-5 w-5" />
      </button>
    );
  }

  // ── Active call: mute + hang-up ──
  return (
    <div className="flex items-center gap-1">
      {/* Mute toggle */}
      <button
        onClick={toggleMute}
        title={muted ? 'Unmute' : 'Mute'}
        className={`p-2 rounded-lg transition-colors ${
          muted
            ? 'text-amber-500 bg-amber-50 dark:bg-amber-900/30 hover:bg-amber-100'
            : 'text-blue-600 bg-blue-50 dark:bg-blue-900/30 hover:bg-blue-100'
        }`}
      >
        {muted ? <MicOff className="h-5 w-5" /> : <Mic className="h-5 w-5" />}
      </button>

      {/* Hang up */}
      <button
        onClick={hangUp}
        title="End call"
        className="p-2 text-white bg-red-500 hover:bg-red-600 rounded-lg transition-colors"
      >
        <PhoneOff className="h-5 w-5" />
      </button>

      {/* Live indicator */}
      <span className="flex items-center gap-1 text-xs font-medium text-green-600 dark:text-green-400">
        <span className="inline-block w-2 h-2 rounded-full bg-green-500 animate-pulse" />
        Live
      </span>
    </div>
  );
}
